import { AIFI_LEVELS, METHODOLOGY_DIMENSIONS, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';

export type ProgramEmailExample = {
  label: string;
  insteadOf: string;
  tryText: string;
};

export type UpskillingProgramTemplate = {
  dimensionKey: string;
  dimensionLabel: string;
  level: AifiLevel;
  objective: string;
  intro: string;
  examples: ProgramEmailExample[];
  checklist: string[];
};

type TemplateContent = Omit<UpskillingProgramTemplate, 'dimensionKey' | 'dimensionLabel' | 'level'>;

function makeTemplate(
  dimensionKey: string,
  level: AifiLevel,
  content: TemplateContent,
): UpskillingProgramTemplate {
  const dimensionLabel =
    METHODOLOGY_DIMENSIONS.find((dimension) => dimension.key === dimensionKey)?.label ?? dimensionKey;

  return {
    dimensionKey,
    dimensionLabel,
    level,
    ...content,
  };
}

function templateKey(dimensionKey: string, level: AifiLevel): string {
  return `${dimensionKey}:${level}`;
}

const TASK_CLARITY: Record<AifiLevel, TemplateContent> = {
  Novice: {
    objective: 'Say what you want the model to produce in the first sentence.',
    intro:
      'Vague asks like "help with this" force the model to guess. Naming the task upfront usually cuts your thread in half.',
    examples: [
      {
        label: 'Example 1 · email',
        insteadOf: 'help me with this email',
        tryText: 'Rewrite this email to sound polite but firm. Keep my meaning. Under 100 words.',
      },
      {
        label: 'Example 2 · summary',
        insteadOf: 'summarise this',
        tryText: 'Summarise this page in 3 bullets: main point, one risk, one next step.',
      },
    ],
    checklist: [
      'Did I name the task (rewrite, summarise, compare, draft)?',
      'Did I say what the output is for?',
      'Could someone else repeat this ask from my first line?',
    ],
  },
  Beginner: {
    objective: 'Name the deliverable and who it is for before you add detail.',
    intro:
      'You already state the task sometimes — the next step is pairing it with audience and purpose so the model does not guess tone or depth.',
    examples: [
      {
        label: 'Example 1 · customer email',
        insteadOf: 'write something back to this client',
        tryText:
          'Draft a reply to this client declining the discount, keeping the relationship warm. Under 120 words.',
      },
      {
        label: 'Example 2 · internal update',
        insteadOf: 'update the team on the project',
        tryText:
          'Write a Slack update for engineering leads: what shipped, what slipped, and what we need from them this week.',
      },
    ],
    checklist: [
      'Have I named the deliverable?',
      'Have I said who will read or use it?',
      'Is the main ask in the first two sentences?',
    ],
  },
  Practitioner: {
    objective: 'Separate the outcome, scope, and what "done" looks like.',
    intro:
      'At your level, the gap is rarely "no goal" — it is ambiguous success. Spell out what good looks like so you do not negotiate quality across four turns.',
    examples: [
      {
        label: 'Example 1 · contract review',
        insteadOf: 'fix this contract',
        tryText:
          'Review this MSA for payment terms over 60 days. Flag each clause and say why it is a risk. Output: numbered list, one line per issue.',
      },
      {
        label: 'Example 2 · analysis',
        insteadOf: 'analyse our churn',
        tryText:
          'Analyse Q3 churn by segment. Done = top 3 drivers with evidence from the data, plus one recommended action per driver.',
      },
    ],
    checklist: [
      'Did I define "done" in concrete terms?',
      'Did I bound scope (what to include / exclude)?',
      'Would two reviewers agree if this output is good enough?',
    ],
  },
  Expert: {
    objective: 'Frame multi-step work with explicit decision points and stop rules.',
    intro:
      'You already write clear single-shot prompts. The edge at Expert is sequencing: tell the model when to pause, what to optimise for, and what not to expand without permission.',
    examples: [
      {
        label: 'Example 1 · strategy memo',
        insteadOf: 'write a strategy doc for the board',
        tryText:
          'Step 1 only: outline a 1-page board memo on EU expansion — sections, thesis per section, open questions. Stop after the outline; do not draft prose until I approve.',
      },
      {
        label: 'Example 2 · code change',
        insteadOf: 'refactor this module',
        tryText:
          'Propose a refactor plan for auth.ts: goals, risks, test impact, smallest shippable slice. Wait for my pick before editing code.',
      },
    ],
    checklist: [
      'Did I separate planning from execution?',
      'Did I name the decision you need from me?',
      'Did I say what not to do without explicit approval?',
    ],
  },
};

const CONTEXT_PROVISION: Record<AifiLevel, TemplateContent> = {
  Novice: {
    objective: 'Paste the source material and say what role you are playing.',
    intro:
      'The model only sees what you give it. A one-line role plus the actual text beats a clever question with no context.',
    examples: [
      {
        label: 'Example 1 · document',
        insteadOf: 'what does this mean',
        tryText:
          'You are helping a new hire understand policy. Here is the paragraph [paste]. Explain it in plain English in 4 sentences.',
      },
      {
        label: 'Example 2 · data',
        insteadOf: 'why did this happen',
        tryText:
          'You are a support lead reviewing tickets. Here are 10 tickets from yesterday [paste]. What is the most common issue?',
      },
    ],
    checklist: [
      'Did I paste or attach the relevant source?',
      'Did I say what role the model should take?',
      'Did I point to the specific part to use?',
    ],
  },
  Beginner: {
    objective: 'Give the role, the source material, and the constraints.',
    intro:
      'Role + source is your baseline. Add one constraint that matters (audience, deadline, tone, or data limits) so the answer lands in the right lane first time.',
    examples: [
      {
        label: 'Example 1 · data question',
        insteadOf: 'why are sales down',
        tryText:
          'Here is last quarter by region [paste]. You are a sales analyst. Which region drove the drop and what is one likely cause?',
      },
      {
        label: 'Example 2 · rewrite',
        insteadOf: 'make this better',
        tryText:
          'You are a product marketer. Rewrite this paragraph for a technical audience. Keep it under 100 words. [paste]',
      },
    ],
    checklist: [
      'Did I give the role or audience?',
      'Did I attach the data or source?',
      'Did I state the constraints that matter?',
    ],
  },
  Practitioner: {
    objective: 'Structure context: stakes, inputs, and what the model should not assume.',
    intro:
      'You provide context — the upgrade is organising it so the model does not fill gaps with generic advice. Lead with stakes, then inputs, then assumptions to avoid.',
    examples: [
      {
        label: 'Example 1 · incident',
        insteadOf: 'help with this outage',
        tryText:
          'Context: P1 checkout outage, 12 min, EU only. Logs attached. You are our SRE on call. Assume nothing about root cause. List top 3 hypotheses ranked by likelihood.',
      },
      {
        label: 'Example 2 · competitive',
        insteadOf: 'how do we compare to X',
        tryText:
          'We sell to mid-market HR. Competitor X just launched AI scheduling. Here is our positioning doc [paste]. Compare on 4 criteria our buyers care about; cite only from the doc.',
      },
    ],
    checklist: [
      'Did I state stakes or decision this supports?',
      'Did I separate facts from opinions in what I pasted?',
      'Did I say what not to invent or assume?',
    ],
  },
  Expert: {
    objective: 'Ship high-signal context — references and constraints over dumps.',
    intro:
      'At Expert level, context is about signal density: give the model exactly what changes the answer, label uncertainty, and point to authoritative sources instead of pasting everything.',
    examples: [
      {
        label: 'Example 1 · policy',
        insteadOf: '[pastes 40 pages]',
        tryText:
          'Decision: approve vendor Y for PHI? Use only sections 3.2 and 4.1 of the DPA [paste those sections]. Flag gaps vs SOC 2 Type II; do not speculate on unprovided annexes.',
      },
      {
        label: 'Example 2 · research',
        insteadOf: 'here is everything on the topic',
        tryText:
          'Thesis to stress-test: "Self-serve reduces CAC in SMB." Cite these 3 internal decks [links]. Challenge with counter-evidence from the data slides only; max 5 bullets.',
      },
    ],
    checklist: [
      'Did I include only context that changes the answer?',
      'Did I label what is uncertain or out of scope?',
      'Did I anchor claims to specific sources?',
    ],
  },
};

const OUTPUT_SPECIFICATION: Record<AifiLevel, TemplateContent> = {
  Novice: {
    objective: 'Say whether you want bullets, a paragraph, or a list.',
    intro:
      'Format is the fastest win: "bullets" vs "paragraph" vs "table" stops the model from giving you the wrong shape and asking you to redo it.',
    examples: [
      {
        label: 'Example 1 · notes',
        insteadOf: 'clean up my notes',
        tryText: 'Turn these notes into 5 bullet points. One idea per bullet. No intro sentence.',
      },
      {
        label: 'Example 2 · reply',
        insteadOf: 'respond to this',
        tryText: 'Reply in 3 short paragraphs: acknowledge, answer, next step.',
      },
    ],
    checklist: [
      'Did I name a format (bullets, table, email, code)?',
      'Did I say roughly how long it should be?',
      'Did I say what sections to include?',
    ],
  },
  Beginner: {
    objective: 'Ask for an output format and a length.',
    intro:
      'Format plus length prevents wall-of-text answers. You do not need a perfect template — a rough cap is enough.',
    examples: [
      {
        label: 'Example 1 · meeting',
        insteadOf: 'notes from this call',
        tryText: 'Summarise this call as 5 bullets: decision, owner, due date. Nothing else.',
      },
      {
        label: 'Example 2 · comparison',
        insteadOf: 'compare these options',
        tryText: 'Compare these two vendors in a 3-row table: price, setup time, support. One sentence recommendation below.',
      },
    ],
    checklist: [
      'Did I name a format?',
      'Did I set a length or item count?',
      'Did I say what to leave out?',
    ],
  },
  Practitioner: {
    objective: 'Specify schema, tone, and what a complete output includes.',
    intro:
      'You specify format — now define the schema (columns, sections, fields) and tone so outputs are comparable across threads and teammates can reuse them.',
    examples: [
      {
        label: 'Example 1 · summary',
        insteadOf: 'summarise this call',
        tryText:
          'Summarise this call as 5 bullets: decision, owner, due date, risks, open questions. Neutral tone. Max 12 words per bullet.',
      },
      {
        label: 'Example 2 · analysis',
        insteadOf: 'compare these vendors',
        tryText:
          'Compare these three vendors in a table: price, SLA, integration effort, security certs. One row each. End with a one-line pick and why.',
      },
    ],
    checklist: [
      'Did I define columns or sections?',
      'Did I set tone (neutral, executive, technical)?',
      'Did I say what "complete" looks like?',
    ],
  },
  Expert: {
    objective: 'Write output contracts — structure, exclusions, and acceptance criteria.',
    intro:
      'Expert prompts treat the output like an API: precise shape, explicit exclusions, and testable acceptance criteria so you can delegate without reformatting.',
    examples: [
      {
        label: 'Example 1 · exec brief',
        insteadOf: 'exec summary please',
        tryText:
          'Output: exactly 4 bullets (Situation, Complication, Resolution, Ask). Max 20 words each. No jargon acronyms. Fail if any bullet exceeds length — revise before sending.',
      },
      {
        label: 'Example 2 · tech spec',
        insteadOf: 'draft the spec',
        tryText:
          'Produce RFC sections 1–3 only: problem, goals/non-goals, proposal. Markdown with H2 headers. Non-goals must list 3 explicit out-of-scope items. Stop at section 3.',
      },
    ],
    checklist: [
      'Are sections and limits machine-checkable?',
      'Did I list explicit exclusions?',
      'Could someone verify completeness without reading my mind?',
    ],
  },
};

const PROMPT_LENGTH: Record<AifiLevel, TemplateContent> = {
  Novice: {
    objective: 'Use one short prompt for one ask.',
    intro:
      'Long prompts often hide the real question. Start with the shortest version that still names the task — you can add detail in the next turn if needed.',
    examples: [
      {
        label: 'Example 1 · question',
        insteadOf: 'so we have this situation with the client and anyway can you help',
        tryText: 'Draft a 3-sentence apology email for a missed deadline. Professional tone.',
      },
      {
        label: 'Example 2 · list',
        insteadOf: 'I need ideas for the offsite and also meals and maybe games and...',
        tryText: 'List 8 offsite activity ideas for 20 people, mixed indoor/outdoor.',
      },
    ],
    checklist: [
      'Is there only one main ask?',
      'Did I cut the backstory that does not change the answer?',
      'Could this prompt fit in two lines?',
    ],
  },
  Beginner: {
    objective: 'One task per prompt — add detail only when it changes the answer.',
    intro:
      'You are past one-liners but may still over-explain. Keep context that changes the output; delete everything that is "nice to know."',
    examples: [
      {
        label: 'Example 1 · research',
        insteadOf: 'tell me about this market',
        tryText: 'In 6 bullets: market size, top 3 players, one risk. Skip the history.',
      },
      {
        label: 'Example 2 · drafting',
        insteadOf: 'write the whole plan',
        tryText: 'Draft the intro section only, 150 words, then stop and wait for my notes.',
      },
    ],
    checklist: [
      'Is every sentence doing work?',
      'Did I cut duplicate instructions?',
      'Did I ask for one thing at a time?',
    ],
  },
  Practitioner: {
    objective: 'Write dense prompts — every line should change the output.',
    intro:
      'Length is not virtue. Practitioners win by packing role, constraints, and format into fewer sentences — not by adding more adjectives.',
    examples: [
      {
        label: 'Example 1 · brief',
        insteadOf: '[long background paragraph] ... so anyway summarise',
        tryText:
          'Audience: CFO. Input: Q3 results [paste]. Output: 4 bullets — revenue, margin, one risk, one ask. No narrative intro.',
      },
      {
        label: 'Example 2 · edit',
        insteadOf: 'here is my life story and the doc, please improve',
        tryText:
          'Tighten this PRD executive summary to 120 words. Keep all numbers. Do not change technical claims. [paste summary only]',
      },
    ],
    checklist: [
      'If I delete a sentence, does the answer change?',
      'Did I paste only the necessary excerpt?',
      'Did I avoid repeating the same constraint twice?',
    ],
  },
  Expert: {
    objective: 'Control scope surgically — expand and contract deliberately.',
    intro:
      'Experts rarely need more words — they need precise scope control: what to omit, when to stop, and how to iterate without re-sending the kitchen sink.',
    examples: [
      {
        label: 'Example 1 · iteration',
        insteadOf: '[re-pastes entire thread context]',
        tryText:
          'Continue from your last outline. Change only section 2: add compliance risks. Do not rewrite sections 1 or 3.',
      },
      {
        label: 'Example 2 · batch',
        insteadOf: 'also fix tone and length and examples and...',
        tryText:
          'Batch edit pass: (1) cut 20% words, (2) replace passive voice, (3) keep all citations. Return tracked-style before/after for paragraph 2 only.',
      },
    ],
    checklist: [
      'Did I reference prior output instead of re-pasting?',
      'Did I limit which parts may change?',
      'Did I separate passes instead of one vague "improve"?',
    ],
  },
};

const CONVERSATION_EFFICIENCY: Record<AifiLevel, TemplateContent> = {
  Novice: {
    objective: 'Say what is wrong with the last answer — not just "try again."',
    intro:
      '"Try again" resets the model with no new information. One specific correction usually gets you there on the next turn.',
    examples: [
      {
        label: 'Example 1 · tone',
        insteadOf: 'no that is wrong, try again',
        tryText: 'Too formal. Same content, friendlier tone, half the length.',
      },
      {
        label: 'Example 2 · missing piece',
        insteadOf: 'that is not what I wanted',
        tryText: 'You missed the deadline date. Add it to bullet 2 and keep everything else.',
      },
    ],
    checklist: [
      'Did I say what to keep from the last answer?',
      'Did I name the one thing to fix?',
      'Did I avoid starting a brand-new prompt from scratch?',
    ],
  },
  Beginner: {
    objective: 'Reference the last output and request one targeted change.',
    intro:
      'Treat the thread as a draft: point at what works, what does not, and the single edit you need next.',
    examples: [
      {
        label: 'Example 1 · troubleshooting',
        insteadOf: 'it still does not work',
        tryText:
          'That did not fix it. Error is still X after step 2. What is the next most likely cause — one step only.',
      },
      {
        label: 'Example 2 · refinement',
        insteadOf: 'can you do that again but better',
        tryText:
          'Keep the structure. Tighten the opening paragraph and remove jargon. If unclear, ask one question before rewriting.',
      },
    ],
    checklist: [
      'Did I say what changed since last turn?',
      'Did I ask for one next step, not a full redo?',
      'Did I quote or point to the part to edit?',
    ],
  },
  Practitioner: {
    objective: 'Give diff-style feedback and checkpoint before expanding scope.',
    intro:
      'Efficiency at your level means editing like code review: line-level feedback, explicit keep/change, and a pause before new requirements mid-thread.',
    examples: [
      {
        label: 'Example 1 · table',
        insteadOf: 'add more columns and also change the intro',
        tryText:
          'Keep columns A–C as-is. Add column D "Owner" from the transcript. Do not change the intro until I confirm the table.',
      },
      {
        label: 'Example 2 · scope',
        insteadOf: 'now also do the competitive analysis',
        tryText:
          'Stop after the pricing section. Before competitive analysis: confirm the pricing table is correct. Then wait.',
      },
    ],
    checklist: [
      'Did I separate "keep" from "change"?',
      'Did I checkpoint before adding new scope?',
      'Did I avoid reopening settled sections?',
    ],
  },
  Expert: {
    objective: 'Batch edits, set stop rules, and accept good-enough when criteria are met.',
    intro:
      'The Expert habit is closing loops: batch related fixes, define when to stop iterating, and do not gold-plate past the acceptance criteria you set.',
    examples: [
      {
        label: 'Example 1 · review',
        insteadOf: 'keep tweaking until it feels right',
        tryText:
          'Two passes max: (1) factual accuracy vs source, (2) clarity. If both pass, stop — do not polish prose further.',
      },
      {
        label: 'Example 2 · delegation',
        insteadOf: 'iterate with me on every paragraph',
        tryText:
          'Deliver v1 meeting all criteria in my first message. List up to 3 assumptions you made. I will reply with block changes only.',
      },
    ],
    checklist: [
      'Did I cap iteration rounds?',
      'Are acceptance criteria explicit?',
      'Am I stopping when criteria are met?',
    ],
  },
};

const CONSTRAINT_SPECIFICATION: Record<AifiLevel, TemplateContent> = {
  Novice: {
    objective: 'Name one thing the model must not do or include.',
    intro:
      'A single "do not" line prevents the most common bad surprises — made-up facts, wrong tone, or off-topic sections.',
    examples: [
      {
        label: 'Example 1 · email',
        insteadOf: 'write a follow-up email',
        tryText: 'Write a follow-up email. Do not mention price or discounts.',
      },
      {
        label: 'Example 2 · summary',
        insteadOf: 'summarise this report',
        tryText: 'Summarise in 5 bullets. Do not recommend actions — facts only.',
      },
    ],
    checklist: [
      'Did I name at least one exclusion?',
      'Did I say what to avoid (tone, topics, format)?',
      'Is the constraint in the first half of the prompt?',
    ],
  },
  Beginner: {
    objective: 'State boundaries or exclusions up front.',
    intro:
      'Must-nots are as important as must-haves. Put exclusions next to the task so the model does not waste a turn on content you will delete.',
    examples: [
      {
        label: 'Example 1 · drafting',
        insteadOf: 'write a post about our launch',
        tryText:
          'Write a 200-word launch post for LinkedIn. Do not mention pricing or competitors. Tone: confident, not hypey.',
      },
      {
        label: 'Example 2 · analysis',
        insteadOf: 'review this proposal',
        tryText:
          'Review this proposal for legal risk only. Ignore formatting. Flag data retention or liability caps.',
      },
    ],
    checklist: [
      'Did I say what to leave out?',
      'Did I name non-negotiable boundaries?',
      'Did I limit the scope of the review?',
    ],
  },
  Practitioner: {
    objective: 'Separate hard constraints from preferences and cite why they matter.',
    intro:
      'Practitioners specify constraints with intent: compliance, brand, audience sensitivity — so the model prioritises correctly when tradeoffs appear.',
    examples: [
      {
        label: 'Example 1 · customer comms',
        insteadOf: 'announce the delay',
        tryText:
          'Announce a 2-week delay to enterprise customers. Hard: no blame on engineering, no new dates. Soft: empathetic tone. Max 150 words.',
      },
      {
        label: 'Example 2 · internal',
        insteadOf: 'write the FAQ',
        tryText:
          'FAQ for sales on the new SKU. Must not quote list price. Must align with attached positioning [paste]. Flag any conflict with the doc.',
      },
    ],
    checklist: [
      'Did I label must-not vs nice-to-have?',
      'Did I explain why a constraint exists when non-obvious?',
      'Did I point to an authoritative source for conflicts?',
    ],
  },
  Expert: {
    objective: 'Layer constraints with precedence — what wins when rules conflict.',
    intro:
      'Expert constraint design handles real tradeoffs: legal vs speed, accuracy vs brevity. Say which rule wins so the model does not guess under pressure.',
    examples: [
      {
        label: 'Example 1 · regulated',
        insteadOf: 'make this shorter but keep it compliant',
        tryText:
          'Shorten 30% for exec readout. Precedence: (1) compliance statements verbatim from legal [paste], (2) brevity, (3) tone. If conflict, stop and list tradeoffs.',
      },
      {
        label: 'Example 2 · technical',
        insteadOf: 'optimise this however you think best',
        tryText:
          'Refactor for latency. Hard: no behaviour change, public API frozen. Soft: readability. If API change seems required, propose only — do not implement.',
      },
    ],
    checklist: [
      'Did I define precedence among constraints?',
      'Did I say what to do when constraints conflict?',
      'Did I forbid silent violations of hard rules?',
    ],
  },
};

const BY_DIMENSION: Record<string, Record<AifiLevel, TemplateContent>> = {
  task_clarity: TASK_CLARITY,
  context_provision: CONTEXT_PROVISION,
  output_specification: OUTPUT_SPECIFICATION,
  prompt_length: PROMPT_LENGTH,
  conversation_efficiency: CONVERSATION_EFFICIENCY,
  constraint_specification: CONSTRAINT_SPECIFICATION,
};

export const UPSKILLING_PROGRAM_TEMPLATES: Record<string, UpskillingProgramTemplate> = Object.fromEntries(
  Object.entries(BY_DIMENSION).flatMap(([dimensionKey, byLevel]) =>
    AIFI_LEVELS.map((level) => [templateKey(dimensionKey, level), makeTemplate(dimensionKey, level, byLevel[level])]),
  ),
);

export function getUpskillingProgramTemplate(
  dimensionKey: string,
  level: AifiLevel,
): UpskillingProgramTemplate | null {
  return UPSKILLING_PROGRAM_TEMPLATES[templateKey(dimensionKey, level)] ?? null;
}

export function formatProgramModalSubtitle(dimensionLabel: string, level: AifiLevel): string {
  return `${dimensionLabel} \u00b7 ${level}`;
}
